import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ClipboardList,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  Calendar,
  Filter,
  RefreshCw,
  Layers,
  History,
} from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import Modal from '../../components/UI/Modal';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import HistoryLogModal from '../../components/History/HistoryLogModal';

interface BOQ {
  _id: string;
  boqNumber: string;
  customer: any;
  projectName?: string;
  projectAddress?: string;
  boqDate: string;
  validUntil?: string;
  status: string;
  parentBOQ?: any;
  version?: number;
  inquiryOf?: any;
  inquiryOfDesignation?: string;
  assignedTo?: any;
  schedules?: any[];
}

const getRefName = (ref: any): string => {
  if (ref == null) return '';
  if (typeof ref === 'object' && ref !== null && typeof ref.name === 'string') return ref.name;
  return String(ref);
};

const getInquiryOfDisplay = (boq: BOQ): string => {
  const name = getRefName(boq.inquiryOf);
  const designation = boq.inquiryOfDesignation || (boq.inquiryOf && typeof boq.inquiryOf === 'object' ? boq.inquiryOf.designation : '');
  if (!name && !designation) return '—';
  return designation ? `${name} (${designation})` : name;
};

const getSchedulesSectionsDisplay = (schedules: any[] | undefined): React.ReactNode => {
  if (!schedules || !schedules.length) return '—';
  return (
    <ul className="text-xs text-gray-600 space-y-1 mt-0.5">
      {schedules.map((sch: any, i: number) => {
        const scheduleName = sch.scheduleName || (sch.schedule && typeof sch.schedule === 'object' ? sch.schedule.name : null) || `Schedule ${i + 1}`;
        const sections = sch.sections || [];
        const sectionNames = sections.map((sec: any, j: number) =>
          sec.sectionName || (sec.section && typeof sec.section === 'object' ? sec.section.name : null) || `Section ${j + 1}`
        );
        return (
          <li key={i} className="flex flex-wrap gap-x-1">
            <span className="font-medium text-gray-700">{scheduleName}</span>
            {sectionNames.length > 0 && (
              <span className="text-gray-500">
                → {sectionNames.join(', ')}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

type CustomerOption = { _id: string; name?: string; company?: string; projects?: { name: string; address?: string }[] };
type EmployeeOption = { _id: string; name?: string; designation?: string };
type UserOption = { _id: string; name?: string; email?: string };
type ScheduleOption = { _id: string; name?: string };
type SectionOption = { _id: string; name?: string; schedules?: string[] | { _id: string }[] };

const BOQsPage: React.FC = () => {
  const [boqs, setBOQs] = useState<BOQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterProjectName, setFilterProjectName] = useState('');
  const [filterInquiryOf, setFilterInquiryOf] = useState('');
  const [filterSchedule, setFilterSchedule] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [schedules, setSchedules] = useState<ScheduleOption[]>([]);
  const [sections, setSections] = useState<SectionOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [customerProjects, setCustomerProjects] = useState<{ name: string }[]>([]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyEntityId, setHistoryEntityId] = useState<string | null>(null);
  const [historyEntityNumber, setHistoryEntityNumber] = useState<string | null>(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [revisionModalBOQ, setRevisionModalBOQ] = useState<BOQ | null>(null);
  const navigate = useNavigate();

  const openPageHistory = () => {
    setHistoryEntityId(null);
    setHistoryEntityNumber(null);
    setHistoryModalOpen(true);
  };
  const openItemHistory = (id: string, number: string) => {
    setHistoryEntityId(id);
    setHistoryEntityNumber(number);
    setHistoryModalOpen(true);
  };

  useEffect(() => {
    Promise.all([
      apiService.getAllCustomers?.() ?? apiService.getCustomers?.({ limit: 0 }) ?? Promise.resolve({ data: [] }),
      apiService.getEmployees?.() ?? Promise.resolve({ data: [] }),
      apiService.getSchedules?.() ?? Promise.resolve({ data: [] }),
      apiService.getSections?.() ?? Promise.resolve({ data: [] }),
      apiService.getUsers?.() ?? Promise.resolve({ data: [] }),
    ]).then(([custRes, empRes, schedRes, secRes, usersRes]) => {
      setCustomers(Array.isArray(custRes.data) ? custRes.data : (custRes.data?.customers ?? []));
      setEmployees(Array.isArray(empRes.data) ? empRes.data : (empRes.data?.employees ?? empRes.data ?? []));
      setSchedules(Array.isArray(schedRes.data) ? schedRes.data : (schedRes.data?.schedules ?? schedRes.data ?? []));
      setSections(Array.isArray(secRes.data) ? secRes.data : (secRes.data?.sections ?? secRes.data ?? []));
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users ?? usersRes.data ?? []));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filterCustomer) {
      setCustomerProjects([]);
      setFilterProjectName('');
      return;
    }
    apiService.getCustomer(filterCustomer)
      .then((res: any) => setCustomerProjects(res.data?.projects?.filter((p: any) => p?.name) ?? []))
      .catch(() => setCustomerProjects([]));
    setFilterProjectName('');
  }, [filterCustomer]);

  const sectionsForSchedule = useMemo(() => {
    if (!filterSchedule) return [];
    return sections.filter((s) => {
      const schedIds = (s.schedules ?? []).map((sch: any) => (typeof sch === 'object' ? sch?._id : sch));
      return schedIds.includes(filterSchedule);
    });
  }, [sections, filterSchedule]);

  useEffect(() => {
    setFilterSection('');
  }, [filterSchedule]);

  const fetchBOQs = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (filterCustomer) params.customer = filterCustomer;
      if (filterProjectName) params.projectName = filterProjectName;
      if (filterInquiryOf) params.inquiryOf = filterInquiryOf;
      if (filterSchedule) params.schedule = filterSchedule;
      if (filterSection) params.section = filterSection;
      const response = await apiService.getBOQs(params);
      setBOQs(response.data || []);
    } catch (error) {
      console.error('Error fetching BOQs:', error);
      toast.error('Failed to fetch BOQs');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, filterCustomer, filterProjectName, filterInquiryOf, filterSchedule, filterSection]);

  useEffect(() => {
    fetchBOQs();
  }, [fetchBOQs]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this BOQ?')) return;
    try {
      await apiService.deleteBOQ(id);
      toast.success('BOQ deleted successfully');
      fetchBOQs();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete BOQ');
    }
  };

  const groupedBOQs = useMemo(() => {
    const originals: (BOQ & { revisions?: BOQ[] })[] = [];
    const revisionsMap = new Map<string, BOQ[]>();
    boqs.forEach((boq) => {
      if (boq.parentBOQ) {
        const parentId = typeof boq.parentBOQ === 'object' ? boq.parentBOQ._id : boq.parentBOQ;
        if (!revisionsMap.has(parentId)) revisionsMap.set(parentId, []);
        revisionsMap.get(parentId)!.push(boq);
      } else {
        originals.push(boq as BOQ & { revisions?: BOQ[] });
      }
    });
    originals.forEach((original) => {
      const revisions = revisionsMap.get(original._id) || [];
      if (revisions.length > 0) {
        original.revisions = revisions.sort((a, b) => (a.version || 0) - (b.version || 0));
      }
    });
    return originals;
  }, [boqs]);

  const filteredBOQs = groupedBOQs.filter((boq) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      boq.boqNumber.toLowerCase().includes(term) ||
      (boq.projectName && boq.projectName.toLowerCase().includes(term)) ||
      (typeof boq.customer === 'object' && boq.customer?.name?.toLowerCase().includes(term)) ||
      (boq.revisions && boq.revisions.some((rev) => rev.boqNumber.toLowerCase().includes(term)))
    );
  });

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'viewed', label: 'Viewed' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'expired', label: 'Expired' },
  ];

  const statusBadgeClass = (status: string) =>
    status === 'draft'
      ? 'bg-gray-100 text-gray-800'
      : status === 'sent'
      ? 'bg-blue-100 text-blue-800'
      : status === 'viewed'
      ? 'bg-purple-100 text-purple-800'
      : status === 'accepted'
      ? 'bg-green-100 text-green-800'
      : status === 'rejected'
      ? 'bg-red-100 text-red-800'
      : 'bg-yellow-100 text-yellow-800';

  const boqStatusValues = statusOptions.filter((s) => s.value !== 'all').map((s) => s.value);
  const handleBOQStatusChange = async (id: string, newStatus: string) => {
    if (!newStatus || !boqStatusValues.includes(newStatus)) return;
    try {
      await apiService.updateBOQStatus(id, newStatus);
      setBOQs((prev) => prev.map((b) => (b._id === id ? { ...b, status: newStatus } : b)));
      if (revisionModalBOQ?._id === id) setRevisionModalBOQ((prev) => (prev ? { ...prev, status: newStatus } : null));
      toast.success('BOQ status updated');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleAssignBOQ = async (boqId: string, userId: string | null) => {
    try {
      await apiService.updateBOQ(boqId, { assignedTo: userId || null });
      setBOQs((prev) =>
        prev.map((b) => {
          if (b._id !== boqId) return b;
          const assignedUser = userId ? users.find((u) => u._id === userId) : null;
          return { ...b, assignedTo: assignedUser ? { _id: assignedUser._id, name: assignedUser.name, email: assignedUser.email } : null };
        })
      );
      if (revisionModalBOQ?._id === boqId) {
        const assignedUser = userId ? users.find((u) => u._id === userId) : null;
        setRevisionModalBOQ((prev) => (prev ? { ...prev, assignedTo: assignedUser ? { _id: assignedUser._id, name: assignedUser.name, email: assignedUser.email } : null } : null));
      }
      toast.success(userId ? 'BOQ assigned' : 'Assignment cleared');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update assignment');
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
            Bill of Quantities (BOQ)
          </h1>
          <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'var(--font-family)' }}>
            Manage bills of quantities without pricing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            icon={<History className="w-4 h-4" />}
            onClick={openPageHistory}
            title="View full history"
          >
            History
          </Button>
          <Button icon={<Plus className="w-4 h-4" />} onClick={() => navigate('/boqs/create')}>
            Create BOQ
          </Button>
        </div>
      </motion.div>

      <HistoryLogModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        entityType="boq"
        entityId={historyEntityId}
        entityNumber={historyEntityNumber}
        title={historyEntityId ? `History: ${historyEntityNumber}` : 'BOQs history'}
      />

      <Modal
        isOpen={!!revisionModalBOQ}
        onClose={() => setRevisionModalBOQ(null)}
        title="Revised BOQ"
        size="md"
      >
        {revisionModalBOQ && (
          <div className="space-y-4">
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">BOQ Number</p>
                <span className="font-semibold text-gray-900">{revisionModalBOQ.boqNumber}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Status</p>
                <select
                  value={revisionModalBOQ.status}
                  onChange={(e) => handleBOQStatusChange(revisionModalBOQ._id, e.target.value)}
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                >
                  {boqStatusValues.map((value) => (
                    <option key={value} value={value}>
                      {value.charAt(0).toUpperCase() + value.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Customer</p>
                <p className="font-medium text-gray-900">{typeof revisionModalBOQ.customer === 'object' ? getRefName(revisionModalBOQ.customer) : revisionModalBOQ.customer || '—'}</p>
              </div>
              {revisionModalBOQ.projectName && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Project</p>
                  <p className="text-gray-800">{revisionModalBOQ.projectName}</p>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span>{new Date(revisionModalBOQ.boqDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Inquiry of</p>
                <p className="text-gray-600 text-xs">{getInquiryOfDisplay(revisionModalBOQ)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">Schedules & Sections</p>
                <div className="text-xs text-gray-600">{getSchedulesSectionsDisplay(revisionModalBOQ.schedules)}</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                size="sm"
                icon={<Eye className="w-4 h-4" />}
                onClick={() => { navigate(`/boqs/${revisionModalBOQ._id}`); setRevisionModalBOQ(null); }}
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<Edit2 className="w-4 h-4" />}
                onClick={() => { navigate(`/boqs/edit/${revisionModalBOQ._id}`); setRevisionModalBOQ(null); }}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<RefreshCw className="w-4 h-4" />}
                onClick={() => { navigate(`/boqs/${revisionModalBOQ._id}/revise`); setRevisionModalBOQ(null); }}
              >
                Revise
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<History className="w-4 h-4" />}
                onClick={() => { openItemHistory(revisionModalBOQ._id, revisionModalBOQ.boqNumber); setRevisionModalBOQ(null); }}
              >
                History
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:bg-red-50 hover:border-red-200"
                icon={<Trash2 className="w-4 h-4" />}
                onClick={() => { handleDelete(revisionModalBOQ._id); setRevisionModalBOQ(null); }}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card padding="md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Search BOQs by number, customer, or project..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Search className="w-5 h-5" />}
              fullWidth
            />
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<Filter className="w-4 h-4" />}
                onClick={() => setFilterPanelOpen((prev) => !prev)}
                className={filterPanelOpen ? 'bg-gray-100 border-gray-300' : ''}
              >
                Filter
              </Button>
            </div>
          </div>
          {filterPanelOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <select
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              className="min-w-[140px] rounded-lg border border-gray-200 bg-white py-2.5 px-3.5 text-sm font-medium text-gray-700 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 hover:border-gray-300"
            >
              <option value="">All Customers</option>
              {customers.map((c) => (
                <option key={c._id} value={c._id}>{c.name || c.company || c._id}</option>
              ))}
            </select>
            <select
              value={filterProjectName}
              onChange={(e) => setFilterProjectName(e.target.value)}
              disabled={!filterCustomer}
              className="min-w-[140px] rounded-lg border border-gray-200 bg-gray-50 py-2.5 px-3.5 text-sm font-medium text-gray-600 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <option value="">All Projects</option>
              {customerProjects.map((p, i) => (
                <option key={i} value={p.name}>{p.name}</option>
              ))}
            </select>
            <select
              value={filterInquiryOf}
              onChange={(e) => setFilterInquiryOf(e.target.value)}
              className="min-w-[140px] rounded-lg border border-gray-200 bg-white py-2.5 px-3.5 text-sm font-medium text-gray-700 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 hover:border-gray-300"
            >
              <option value="">All Inquiry Of</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>{emp.name}{emp.designation ? ` (${emp.designation})` : ''}</option>
              ))}
            </select>
            <select
              value={filterSchedule}
              onChange={(e) => setFilterSchedule(e.target.value)}
              className="min-w-[140px] rounded-lg border border-gray-200 bg-white py-2.5 px-3.5 text-sm font-medium text-gray-700 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 hover:border-gray-300"
            >
              <option value="">All Schedules</option>
              {schedules.map((s) => (
                <option key={s._id} value={s._id}>{s.name || s._id}</option>
              ))}
            </select>
            <select
              value={filterSection}
              onChange={(e) => setFilterSection(e.target.value)}
              disabled={!filterSchedule}
              className="min-w-[140px] rounded-lg border border-gray-200 bg-gray-50 py-2.5 px-3.5 text-sm font-medium text-gray-600 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <option value="">All Sections</option>
              {sectionsForSchedule.map((s) => (
                <option key={s._id} value={s._id}>{s.name || s._id}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="min-w-[120px] rounded-lg border border-gray-200 bg-white py-2.5 px-3.5 text-sm font-medium text-gray-700 shadow-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 hover:border-gray-300"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setFilterCustomer('');
                setFilterProjectName('');
                setFilterInquiryOf('');
                setFilterSchedule('');
                setFilterSection('');
                setStatusFilter('all');
              }}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 py-2 px-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Clear filters
            </button>
          </div>
          )}
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredBOQs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredBOQs.map((boq) => (
              <motion.div
                key={boq._id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col h-full rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                style={{ fontFamily: 'var(--font-family)' }}
              >
                {/* Accent header */}
                <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">BOQ</p>
                      <Link
                        to={`/boqs/${boq._id}`}
                        className="text-lg font-semibold text-gray-900 hover:text-emerald-600 truncate block transition-colors"
                      >
                        {boq.boqNumber}
                      </Link>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 shadow-sm ${statusBadgeClass(boq.status)}`}>
                      {boq.status.charAt(0).toUpperCase() + boq.status.slice(1)}
                    </span>
                  </div>

                  <div className="space-y-3 text-sm flex-1">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span>{new Date(boq.boqDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Customer</p>
                      <p className="font-medium text-gray-900 truncate">{typeof boq.customer === 'object' ? getRefName(boq.customer) : boq.customer || '—'}</p>
                    </div>
                    {boq.projectName && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Project</p>
                        <p className="text-gray-800 truncate">{boq.projectName}</p>
                      </div>
                    )}
                    {boq.projectAddress && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Address</p>
                        <p className="text-gray-600 text-xs line-clamp-2">{boq.projectAddress}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-0.5">Inquiry of</p>
                      <p className="text-gray-600 text-xs">{getInquiryOfDisplay(boq)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1 flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" /> Schedules & Sections
                      </p>
                      <div className="text-xs text-gray-600 space-y-0.5">{getSchedulesSectionsDisplay(boq.schedules)}</div>
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-gray-100 space-y-3 -mx-5 -mb-5 px-5 pb-5 bg-gray-50/50 rounded-b-xl">
                    <div className="flex flex-nowrap items-center gap-0.5 min-w-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0 text-blue-600 hover:bg-blue-50"
                      icon={<Eye className="w-4 h-4" />}
                      onClick={() => navigate(`/boqs/${boq._id}`)}
                      title="View"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0 text-amber-600 hover:bg-amber-50"
                      icon={<Edit2 className="w-4 h-4" />}
                      onClick={() => navigate(`/boqs/edit/${boq._id}`)}
                      title="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0 text-purple-600 hover:bg-purple-50"
                      icon={<RefreshCw className="w-4 h-4" />}
                      onClick={() => navigate(`/boqs/${boq._id}/revise`)}
                      title="Revise"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0 text-slate-600 hover:bg-slate-50"
                      icon={<History className="w-4 h-4" />}
                      onClick={() => openItemHistory(boq._id, boq.boqNumber)}
                      title="History"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0 text-red-600 hover:bg-red-50"
                      icon={<Trash2 className="w-4 h-4" />}
                      onClick={() => handleDelete(boq._id)}
                      title="Delete"
                    />
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">Assigned to</label>
                    <select
                      value={(boq as BOQ).assignedTo ? (typeof (boq as BOQ).assignedTo === 'object' ? (boq as BOQ).assignedTo._id : (boq as BOQ).assignedTo) : ''}
                      onChange={(e) => handleAssignBOQ(boq._id, e.target.value || null)}
                      className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    >
                      <option value="">— Unassigned —</option>
                      {Array.isArray(users) && users.map((u) => (
                        <option key={u._id} value={u._id}>
                          {u.name || u.email || u._id}
                        </option>
                      ))}
                    </select>
                  </div>

                  {boq.revisions && boq.revisions.length > 0 && (
                    <div className="pt-3 border-t border-gray-50">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1.5">Revisions</p>
                      <div className="flex flex-wrap gap-1.5">
                        {boq.revisions.map((rev) => (
                          <button
                            key={rev._id}
                            type="button"
                            onClick={() => setRevisionModalBOQ(rev)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-50 text-violet-700 text-xs font-medium hover:bg-violet-100 transition-colors"
                          >
                            <RefreshCw className="w-3 h-3" />
                            {rev.boqNumber}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card padding="md">
            <div className="px-6 py-12 text-center">
              <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                No BOQs yet
              </h3>
              <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
                Get started by creating your first bill of quantities.
              </p>
              <div className="mt-6">
                <Button icon={<Plus className="w-4 h-4" />} onClick={() => navigate('/boqs/create')}>
                  Create BOQ
                </Button>
              </div>
            </div>
          </Card>
        )}
      </motion.div>
    </div>
  );
};

export default BOQsPage;
